import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { logger } from '@/core/logger'
import { Button } from '@/shared/ui'

/**
 * Per-route error boundary. Catches a render error in a single feature page
 * and renders a compact recovery panel inside the main content area — the
 * sidebar + topbar stay live so the user can navigate away without reloading
 * the whole app. Complements `RootErrorBoundary`, which catches anything that
 * escapes the route boundaries.
 *
 * Sentry's integration captures the error with the React component stack and
 * any active user scope; `logger.e` mirrors to the console in dev so the
 * engineer sees the trace without opening Sentry.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <Fallback error={error} resetError={resetError} />
      )}
      onError={(error) => {
        logger.e('Route error boundary caught render error', error)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

function Fallback({
  error,
  resetError,
}: {
  error: unknown
  resetError: () => void
}) {
  const navigate = useNavigate()
  const message =
    error instanceof Error ? error.message : 'Error inesperado cargando la página'
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="text-lg font-semibold">No se pudo cargar esta pantalla</div>
      <div className="max-w-md text-sm text-muted">{message}</div>
      <div className="flex gap-2">
        <Button onClick={resetError}>Reintentar</Button>
        <Button
          onClick={() => {
            resetError()
            navigate('/', { replace: true })
          }}
          variant="ghost"
        >
          Ir al inicio
        </Button>
      </div>
    </div>
  )
}
